import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { PortalShell } from "../components/PortalShell";
import { Button } from "../components/ui/button";
import { ApiError } from "../lib/api";
import { listMigrations, type MigrationJob } from "../lib/migrations";
import { listTemplates, type TemplateSummary } from "../lib/templates";
import { useAuthStore } from "../stores/auth";

const RECENT_LIMIT = 5;

export function DashboardPage(): JSX.Element {
  const user = useAuthStore((state) => state.user);

  const { data, isLoading, error } = useQuery({
    queryKey: ["templates", "recent", RECENT_LIMIT],
    // Ask for the true workspace total alongside the recent page — the stat
    // card showed the capped page length (≤5) as if it were the total (QA-019).
    queryFn: () => listTemplates({ limit: RECENT_LIMIT, sort: "-updated_at", withTotal: true }),
  });

  const templates: TemplateSummary[] = Array.isArray(data?.data) ? data.data : [];
  // Prefer the server-reported total; fall back to the page length only if the
  // API omitted it (older backend), so the card is never blatantly wrong.
  const totalTemplates = data?.pagination?.total ?? templates.length;

  const { data: importsData } = useQuery({
    queryKey: ["migrations", "recent", RECENT_LIMIT],
    queryFn: () => listMigrations({ limit: RECENT_LIMIT }),
  });
  const recentImports: MigrationJob[] = importsData?.data ?? [];

  return (
    <PortalShell>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <p className="text-sm text-neutral-500">Workspace</p>
          <h1 className="text-3xl font-bold text-neutral-900">
            Welcome{user?.name !== null && user?.name !== undefined ? `, ${user.name}` : " back"}.
          </h1>
        </div>
        <Link to="/templates">
          <Button variant="primary">+ New template</Button>
        </Link>
      </header>

      <section
        aria-label="Quick stats"
        className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StatCard label="Templates" value={statValue(totalTemplates, isLoading)} hint="Total in workspace" />
        <StatCard label="API requests" value="—" hint="Last 30 days (T2.5)" />
        <StatCard label="AI generations" value="—" hint="Plan limit applies (T2.5)" />
        <StatCard label="Team members" value="1" hint="Including you (T2.6)" />
      </section>

      <section
        aria-label="Recent templates"
        className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
      >
        <header className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">Recent templates</h2>
          <Link to="/templates" className="text-sm text-primary-600 hover:underline">
            View all
          </Link>
        </header>

        {isLoading ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : error instanceof ApiError ? (
          <ErrorBanner detail={error.problem.detail || error.problem.title} />
        ) : templates.length === 0 ? (
          <EmptyTemplates />
        ) : (
          <ul className="divide-y divide-neutral-200">
            {templates.map((tpl) => (
              <RecentTemplateRow key={tpl.id} template={tpl} />
            ))}
          </ul>
        )}
      </section>

      {recentImports.length > 0 && (
        <section
          aria-label="Recent imports"
          className="mt-8 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
        >
          <header className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-900">Recent imports</h2>
            <Link to="/imports" className="text-sm text-primary-600 hover:underline">
              View all
            </Link>
          </header>
          <ul className="divide-y divide-neutral-200">
            {recentImports.map((job) => (
              <RecentImportRow key={job.id} job={job} />
            ))}
          </ul>
        </section>
      )}
    </PortalShell>
  );
}

function statValue(value: number, isLoading: boolean): string {
  if (isLoading) return "…";
  return new Intl.NumberFormat().format(value);
}

interface StatCardProps {
  label: string;
  value: string;
  hint: string;
}

function StatCard({ label, value, hint }: StatCardProps): JSX.Element {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <p className="text-sm text-neutral-500">{label}</p>
      <p className="mt-1 text-3xl font-semibold text-neutral-900">{value}</p>
      <p className="mt-1 text-xs text-neutral-500">{hint}</p>
    </div>
  );
}

function RecentTemplateRow({ template }: { template: TemplateSummary }): JSX.Element {
  const updated = formatDate(template.updated_at);
  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-neutral-900">
          {template.name}
        </p>
        <p className="truncate text-xs text-neutral-500">
          Updated {updated}
          {template.template_type !== null && template.template_type !== undefined && (
            <> · {template.template_type}</>
          )}
        </p>
      </div>
      <Link to={`/templates`} className="shrink-0 text-sm text-primary-600 hover:underline">
        Open
      </Link>
    </li>
  );
}

const IMPORT_STATUS_STYLE: Record<MigrationJob["status"], string> = {
  completed: "bg-success-50 text-success-700",
  failed: "bg-error-50 text-error-700",
  in_progress: "bg-warning-50 text-warning-700",
  queued: "bg-warning-50 text-warning-700",
  cancelled: "bg-neutral-100 text-neutral-600",
};

function RecentImportRow({ job }: { job: MigrationJob }): JSX.Element {
  const succeeded = job.succeeded_files ?? 0;
  const failed = job.failed_files ?? 0;
  const total = job.total_files ?? 0;
  const isSingle = total <= 1 && job.template_id != null && job.template_id !== "";
  const summary = isSingle
    ? "1 template imported"
    : `${succeeded} converted, ${failed} failed`;

  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${IMPORT_STATUS_STYLE[job.status]}`}
          >
            {job.status.replace("_", " ")}
          </span>
          <span className="truncate text-sm font-medium capitalize text-neutral-900">
            {job.source_platform}
          </span>
        </p>
        <p className="mt-0.5 truncate text-xs text-neutral-500">{summary}</p>
      </div>
      <Link to="/imports" className="shrink-0 text-sm text-primary-600 hover:underline">
        View
      </Link>
    </li>
  );
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function EmptyTemplates(): JSX.Element {
  return (
    <div className="rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center">
      <p className="text-sm font-medium text-neutral-700">No templates yet.</p>
      <p className="mt-1 text-xs text-neutral-500">
        Create your first template to see it appear here.
      </p>
      <Link to="/templates" className="mt-4 inline-block">
        <Button variant="primary" size="sm">
          + New template
        </Button>
      </Link>
    </div>
  );
}

function ErrorBanner({ detail }: { detail: string }): JSX.Element {
  return (
    <p
      role="alert"
      className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
    >
      Couldn&apos;t load templates: {detail}
    </p>
  );
}
